import React, { useEffect, useRef, useState } from 'react';
import { getEmployees, getOperatingRooms, getHospitals, searchShifts, searchOperatingRoomSchedules, searchAllocationsByWindow, updateAllocations } from '../services/api.ts';
import { localDateToUtcStart, localDateToUtcEnd, utcToLocalTime, getLocalDateString } from '../utils/timezone.ts';
import LockToggle from './LockToggle.tsx';
import { colorForHospital, darkenHex } from './hospitalColors.ts';
import { loadSiteLocks, setSiteLock } from './siteLocks.ts';
import { needsNerveBlock, NAMEPLATE_COLORS } from './staffNameplates.ts';
import { loadFloatCrnas, floatSitesByEmployee, type FloatCrnaMap } from './floatCrnas.ts';
import { hospitalOrderFromRooms, compareByHospitalOrder } from './boardViewHelpers.ts';
import { groupTransferCandidates } from './roomTransfers.ts';
import { loadNurseTransfers, effectiveNurseIdsForRoom, saveNurseTransfers } from './nurseTransfers.ts';
import { moveNurseToRoom, moveNurseToRooms, swapNurseRooms, nurseEditsForOverlay, type RawCrnaByRoom } from './nurseRoomMoves.ts';
import { appendMove } from './moveLog.ts';
import { buildNurseEditPayload, nurseEditsFromUndo, NurseUndoChange, buildDoctorEditPayload, editsFromUndo, DoctorUndoChange } from './doctorMoves.ts';
import { pushUndo, popUndo, loadUndoStack, MoveUndoEntry } from './moveUndo.ts';
import { commitAllocationEdit, editedScheduleIds, GuardableAllocation } from './allocationWriteGuard.ts';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import EmployeeDetailsPopover, { normalizeDoctorStatuses } from './EmployeeDetailsPopover.tsx';
import type { SiteCoinOption, AssignedRoomInfo, DoctorDayStatus } from './EmployeeDetailsPopover.tsx';
import type { Employee } from '../services/api.ts';

// ---------------------------------------------------------------------------
// CRNA tab.
//
// A running list of every CRNA working the selected day, grouped and color-coded by the SITE they're
// assigned to (their allocated room's hospital), with unassigned CRNAs in their own group. Each site
// header carries small chips of the anesthesiologist names also assigned to that site. Columns:
// Full Name · In · Out · Shift · Location · Notes.
//
// Like the Anesthesiologists tab, the list starts LOCKED; unlock to edit the notes,
// auto-locks after 30s idle (or lock manually). Notes persist locally per day (survive refresh).
// NOTE: no North/South/Children's zones in this version — organized by site + shift only.
// ---------------------------------------------------------------------------

interface EmployeeLike { employeeId: string; employeeType: string; firstName: string; lastName: string; homeHospitalId?: string; credentials?: string[]; }
interface ShiftLike { employeeId: string; startTimeUtc?: string; endTimeUtc?: string; order?: number | null; }
interface HospitalLike { hospitalId: string; name: string; color?: string; }
interface RoomLike { operatingRoomId: string; hospitalId: string; credential?: string; name?: string; roomNumber?: string; }
interface ScheduleLike { scheduleId: string; operatingRoomId: string; startTimeUtc: string; endTimeUtc: string; surgeryType?: string; }
interface AllocationLike { operatingRoomScheduleId: string; allocatedDoctorIds?: string[]; allocatedNurseIds?: string[]; }

interface Anno { notes: Record<string, string>; }
const EMPTY_ANNO: Anno = { notes: {} };
const crnaAnnoKey = (date: string) => `oc-crna-${date}`;
const loadAnno = (date: string): Anno => {
  try {
    const raw = localStorage.getItem(crnaAnnoKey(date));
    if (raw) {
      const parsed = JSON.parse(raw) || {};
      if (parsed.notes) return { notes: { ...parsed.notes } };
      // Migrate the older two-column {chief, public} shape into a single notes map so nothing typed is lost.
      const merged: Record<string, string> = {};
      const fold = (m?: Record<string, string>) => {
        for (const [id, txt] of Object.entries(m || {})) {
          if (!txt) continue;
          merged[id] = merged[id] ? `${merged[id]} · ${txt}` : txt;
        }
      };
      fold(parsed.chief); fold(parsed.public);
      return { notes: merged };
    }
  } catch { /* ignore */ }
  return EMPTY_ANNO;
};

const AUTO_LOCK_MS = 30000;
const UNASSIGNED = '__unassigned__';
// A CRNA placed into a site's FLOAT slot always reports at 06:00 local — their assignment for the day is "float"
// (the whole-site relief runner), so it isn't derived from any one case's start.
const FLOAT_IN_HM = '06:00';
// A pecking-order CRNA we don't need for the day comes in at the 11:30 bench (blank assignment, bullpen),
// mirroring the doctor tab. Only benched PECKING CRNAs — shift-workers keep their real shift start. (Braden 8/5)
const BENCH_IN_HM = '11:30';
// Which list layout the tab shows: 'site' (default — grouped by hospital) or 'group' (shift work vs pecking
// order, mirroring the Anesthesiologists tab) so report-in times can be compared straight down an ordered list.
const VIEW_MODE_KEY = 'oc-crna-view-mode';

const to12h = (hhmm: string): string => {
  if (!hhmm || hhmm.length < 4) return hhmm || '';
  let h = parseInt(hhmm.slice(0, 2), 10);
  const m = hhmm.slice(3, 5);
  const ap = h >= 12 ? 'p' : 'a';
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${m}${ap}`;
};
const localHM = (utc?: string): string => (utc ? utcToLocalTime(utc) : '');
const localMin = (utc?: string): number => { const t = localHM(utc); return t ? (+t.slice(0, 2)) * 60 + (+t.slice(3, 5)) : 99999; };
const REPORT_IN_LEAD_MIN = 30;         // report at least 30 min before the first case (mirrors the Anesthesiologists tab)
const NERVE_BLOCK_LEAD_MIN = 45;       // 45 min if that first case needs a nerve block

// A short site tag from a hospital name (e.g. "County Hospital 1" -> "CH1"), same as the Anesthesiologists tab.
const siteTag = (name?: string): string => {
  if (!name) return '';
  const words = name.replace(/[’']/g, '').split(/\s+/).filter(Boolean);
  const letters = words.map(w => (w.toUpperCase() === 'SC' ? 'SC' : w[0].toUpperCase())).join('');
  return letters.slice(0, 5);
};

// A short shift descriptor from the shift window (real data — not a fabricated A/B/E taxonomy).
const shiftLabel = (s?: ShiftLike): string => {
  if (!s || !s.startTimeUtc || !s.endTimeUtc) return '';
  const startM = localMin(s.startTimeUtc), endM = localMin(s.endTimeUtc);
  if (startM === endM) return ''; // placeholder shift
  return `${to12h(localHM(s.startTimeUtc))}–${to12h(localHM(s.endTimeUtc))}`;
};

const CrnaView: React.FC = () => {
  const { isAdmin } = useAuth();
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    try { return localStorage.getItem('oc-board-selected-date') || getLocalDateString(); } catch { return getLocalDateString(); }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState<EmployeeLike[]>([]);
  const [shifts, setShifts] = useState<ShiftLike[]>([]);
  const [hospitals, setHospitals] = useState<HospitalLike[]>([]);
  const [rooms, setRooms] = useState<RoomLike[]>([]);
  const [schedules, setSchedules] = useState<ScheduleLike[]>([]);
  const [allocations, setAllocations] = useState<AllocationLike[]>([]);
  const [allocationsLoaded, setAllocationsLoaded] = useState<boolean>(false);
  const [undoStack, setUndoStack] = useState<MoveUndoEntry[]>(() => loadUndoStack(selectedDate));

  const [locked, setLocked] = useState(true);
  const [anno, setAnno] = useState<Anno>(() => loadAnno(selectedDate));
  const [siteLocks, setSiteLocks] = useState<Record<string, string>>(() => loadSiteLocks(selectedDate));
  // Per-day CRNA-transfer overlay (operatingRoomId -> toNurseId) — the SAME store the Board writes, so a swap made
  // on either surface shows on the other. Dispositions (Meeting/Request/Off + note) live in the shared oc-anes store.
  const [nurseTransfers, setNurseTransfers] = useState<Record<string, string>>(() => loadNurseTransfers(selectedDate));
  // Per-day FLOAT-CRNA overlay (hospitalId -> who's in each float slot) — the SAME store the Board writes, so a
  // float placed on the board shows here as assigned-to-float (not unassigned), reloaded live via the storage event.
  const [floatCrnas, setFloatCrnas] = useState<FloatCrnaMap>(() => loadFloatCrnas(selectedDate));
  const [anesStatus, setAnesStatus] = useState<Record<string, DoctorDayStatus[]>>({});
  const [anesNotes, setAnesNotes] = useState<Record<string, string>>({});
  const [anesInTime, setAnesInTime] = useState<Record<string, string>>({}); // manual report-in overrides (shared oc-anes store, same as the Anesthesiologists tab)
  const [viewMode, setViewMode] = useState<'site' | 'group'>(() => {
    try { return localStorage.getItem(VIEW_MODE_KEY) === 'group' ? 'group' : 'site'; } catch { return 'site'; }
  });
  const lockTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { try { localStorage.setItem('oc-board-selected-date', selectedDate); } catch { /* ignore */ } }, [selectedDate]);
  useEffect(() => { try { localStorage.setItem(VIEW_MODE_KEY, viewMode); } catch { /* ignore */ } }, [viewMode]);
  useEffect(() => {
    // Reload every per-day overlay this tab now shares with the Board + Anesthesiologists tab, and keep them LIVE:
    // the storage event fires when the Board (in another window) writes a swap or a disposition, so the tab updates
    // without a manual refresh — the mirror the doctor card already has on the Anesthesiologists tab.
    const reload = () => {
      setAnno(loadAnno(selectedDate));
      setSiteLocks(loadSiteLocks(selectedDate));
      setNurseTransfers(loadNurseTransfers(selectedDate));
      setFloatCrnas(loadFloatCrnas(selectedDate));
      setUndoStack(loadUndoStack(selectedDate));
      try {
        const raw = localStorage.getItem(`oc-anes-${selectedDate}`);
        const p = raw ? JSON.parse(raw) : {};
        const st: Record<string, DoctorDayStatus[]> = {};
        for (const id of Object.keys(p.status || {})) { const l = normalizeDoctorStatuses(p.status[id]); if (l.length) st[id] = l; }
        setAnesStatus(st); setAnesNotes(p.notes || {}); setAnesInTime(p.inTime || {});
      } catch { setAnesStatus({}); setAnesNotes({}); setAnesInTime({}); }
    };
    reload();
    setLocked(true);
    window.addEventListener('focus', reload);
    const onStorage = (e: StorageEvent) => { if (e.key === null || (e.key && e.key.includes(selectedDate))) reload(); };
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('focus', reload); window.removeEventListener('storage', onStorage); };
  }, [selectedDate]);
  // Can this provider work at a site? (open room, or a room whose credential they hold.) Used to WARN before a lock.
  const canWorkAtSite = (empId: string, hospitalId: string): boolean => {
    const creds = new Set(employees.find(e => e.employeeId === empId)?.credentials || []);
    const roomsAtH = rooms.filter(r => r.hospitalId === hospitalId);
    return roomsAtH.length > 0 && roomsAtH.some(r => !r.credential || creds.has(r.credential));
  };
  const setLock = (empId: string, hospitalId: string | null) => {
    if (hospitalId && !canWorkAtSite(empId, hospitalId)) {
      const emp = employees.find(e => e.employeeId === empId);
      const hname = hospitals.find(h => h.hospitalId === hospitalId)?.name || hospitalId;
      if (!window.confirm(`${emp ? `${emp.firstName} ${emp.lastName}` : 'This CRNA'} is not credentialed at ${hname}, so a hard lock there would leave them UNSTAFFED for the day. Lock them anyway?`)) return;
    }
    setSiteLocks(setSiteLock(selectedDate, empId, hospitalId)); bumpAutoLock();
  };
  useEffect(() => () => { if (lockTimer.current) clearTimeout(lockTimer.current); }, []);

  const bumpAutoLock = () => {
    if (lockTimer.current) clearTimeout(lockTimer.current);
    lockTimer.current = setTimeout(() => setLocked(true), AUTO_LOCK_MS);
  };
  // This tab writes allocations (PUT /allocations is withPaidAdmin), and every mutating control sits behind
  // `locked` — so refusing to unlock for a read-only account gates the whole editing surface in one place.
  const unlock = () => { if (!isAdmin) return; setLocked(false); bumpAutoLock(); };
  const lockNow = () => { if (lockTimer.current) clearTimeout(lockTimer.current); setLocked(true); };
  const mutateAnno = (fn: (prev: Anno) => Anno) => {
    setAnno(prev => {
      const next = fn(prev);
      try { localStorage.setItem(crnaAnnoKey(selectedDate), JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    bumpAutoLock();
  };
  const setNote = (id: string, t: string) => mutateAnno(p => ({ notes: { ...p.notes, [id]: t } }));

  // Dispositions (Meeting / Request / Off) — the SAME controls the Board + Anesthesiologists tab expose. They live
  // in the shared `oc-anes-<date>` store, read-modify-write so the other surfaces' fields (order, in-times, doctor
  // dispositions) survive; the legacy `meeting` boolean is kept in sync with status==='meeting'. Mirrors BoardView.
  const writeAnes = (mutate: (prev: any) => any) => {
    try {
      const raw = localStorage.getItem(`oc-anes-${selectedDate}`);
      const prev = { order: {}, notes: {}, meeting: {}, inTime: {}, status: {}, ...(raw ? JSON.parse(raw) : {}) };
      const next = mutate(prev);
      localStorage.setItem(`oc-anes-${selectedDate}`, JSON.stringify(next));
      const st: Record<string, DoctorDayStatus[]> = {};
      for (const id of Object.keys(next.status || {})) { const l = normalizeDoctorStatuses(next.status[id]); if (l.length) st[id] = l; }
      setAnesStatus(st); setAnesNotes(next.notes || {}); setAnesInTime(next.inTime || {});
    } catch { /* ignore */ }
  };
  const toggleAnesStatus = (id: string, status: DoctorDayStatus) => writeAnes((prev: any) => {
    const s = { ...(prev.status || {}) };
    const cur = normalizeDoctorStatuses(s[id]);
    const nextList = cur.includes(status) ? cur.filter((x: DoctorDayStatus) => x !== status) : [...cur, status];
    if (nextList.length) s[id] = nextList; else delete s[id];
    return { ...prev, status: s, meeting: { ...(prev.meeting || {}), [id]: nextList.includes('meeting') } };
  });
  const setAnesNote = (id: string, note: string) => writeAnes((prev: any) => ({ ...prev, notes: { ...(prev.notes || {}), [id]: note } }));
  // Manual report-in override for a CRNA (mirrors the Anesthesiologists tab's setInTime). '' clears it,
  // so the row falls back to the derived report-in. Shared oc-anes store -> shows on the board/doctor tab too.
  const setCrnaInTime = (id: string, hhmm: string) => writeAnes((prev: any) => {
    const inTime = { ...(prev.inTime || {}) };
    if (hhmm) inTime[id] = hhmm; else delete inTime[id];
    return { ...prev, inTime };
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const start = localDateToUtcStart(selectedDate);
        const end = localDateToUtcEnd(selectedDate);
        const [emp, rm, hosp, sh, sc] = await Promise.all([
          getEmployees(), getOperatingRooms(), getHospitals(), searchShifts(start, end), searchOperatingRoomSchedules(start, end),
        ]);
        if (cancelled) return;
        setEmployees(emp.employees || []);
        setRooms(rm.operatingRooms || []);
        setHospitals(hosp.hospitals || []);
        setShifts(sh.shifts || []);
        setSchedules(sc.schedules || []);
        try {
          const al = await searchAllocationsByWindow(start, end);
          if (!cancelled) { setAllocations(al.allocations || []); setAllocationsLoaded(true); }
        // allocationsLoaded gates every writer (buildNurseEditPayload) so a FAILED fetch can never be mistaken
        // for an empty day and delete it via the delete-and-rewrite PUT /allocations.
        } catch (e) { if (!cancelled) { setAllocations([]); setAllocationsLoaded(false); console.error('allocations fetch failed', e); } }
      } catch (e) {
        if (!cancelled) { setError('Failed to load data'); console.error(e); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDate]);

  // ---- Derived lookups ----
  const hospitalNameById = new Map(hospitals.map(h => [h.hospitalId, h.name]));
  const hospitalById = new Map(hospitals.map(h => [h.hospitalId, h]));
  const roomById = new Map(rooms.map(r => [r.operatingRoomId, r]));

  // Click-to-lock site "coin" (mirrors the Anesthesiologists tab): the coin is ALWAYS clickable — a native
  // list of every site is layered invisibly over it, so clicking anywhere picks a site to HARD-LOCK this
  // CRNA there for the day (applied on the next Allocate run). No need to unlock the list; keeps the
  // credential warning inside setLock. Render FUNCTION (not an inner component) — matching
  // AnesthesiologistsView — so it isn't remounted (losing the native select's open state) on every render.
  // A hospital "coin": the site acronym on the hospital's own color (same coin the Anesthesiologists tab + Board use).
  const renderSitePill = (hospitalId: string): JSX.Element => {
    const h = hospitalById.get(hospitalId);
    const name = h?.name || '';
    const color = colorForHospital(h);
    return <span key={hospitalId} title={name} style={{ fontSize: '0.7em', fontWeight: 800, letterSpacing: '0.3px', padding: '1px 7px', borderRadius: '10px', background: color, color: '#fff', border: `1px solid ${darkenHex(color, 0.2)}`, textShadow: '0 1px 1px rgba(0,0,0,0.35)', whiteSpace: 'nowrap' }}>{siteTag(name)}</span>;
  };
  // The site coin — mirrors the Anesthesiologists tab: the coin(s) of the site(s) this CRNA is AT (hospital-colored
  // acronym), with a small lock badge ONLY when a hard lock is set (no persistent lock-emoji placeholder). The whole
  // coin is a click-to-lock control — an invisible native <select> is layered over it.
  const renderSiteCoin = (id: string, sites: string[]): JSX.Element => {
    const lockHid = siteLocks[id];
    const lockColor = lockHid ? colorForHospital(hospitalById.get(lockHid)) : '';
    return (
      <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
        title="Click to lock this CRNA to a site for the day — they'll be used only there on the next Allocate run">
        {lockHid && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '0.7em', fontWeight: 800, letterSpacing: '0.3px', padding: '1px 7px', borderRadius: '10px',
            background: lockColor, color: '#fff', border: `1px solid ${darkenHex(lockColor, 0.2)}`, textShadow: '0 1px 1px rgba(0,0,0,0.35)', whiteSpace: 'nowrap' }}>
            <span aria-hidden style={{ fontSize: '0.9em' }}>🔒</span>{siteTag(hospitalNameById.get(lockHid))}
          </span>
        )}
        {sites.map(hid => renderSitePill(hid))}
        {!lockHid && sites.length === 0 && (
          <span style={{ fontSize: '0.72em', fontWeight: 700, color: 'var(--text-muted)', padding: '1px 7px', borderRadius: '10px', border: '1px dashed var(--border-subtle)', whiteSpace: 'nowrap' }}>lock site ▾</span>
        )}
        <select value={lockHid || ''} onChange={ev => setLock(id, ev.target.value || null)}
          aria-label="Lock this CRNA to a site for the day"
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', border: 0, padding: 0, margin: 0 }}>
          <option value="">No site lock</option>
          {hospitals.map(h => <option key={h.hospitalId} value={h.hospitalId}>{h.name}</option>)}
        </select>
      </span>
    );
  };
  const scheduleById = new Map(schedules.map(s => [s.scheduleId, s]));
  const employeeById = new Map(employees.map(e => [e.employeeId, e]));

  // Float overlay -> per-CRNA float sites. A float CRNA is pre-assigned to a SITE's relief pool (not a room),
  // so they never show up in the allocations; without this they'd fall into "Unassigned". They count as
  // assigned-to-float at that site and report at FLOAT_IN_HM.
  const floatSitesByEmp = floatSitesByEmployee(floatCrnas);
  const isFloatAtSite = (id: string, hospitalId: string): boolean => !!floatSitesByEmp.get(id)?.has(hospitalId);
  const isFloatAnywhere = (id: string): boolean => (floatSitesByEmp.get(id)?.size ?? 0) > 0;

  // Apply the shared NURSE-transfer overlay so this tab reads a room's CRNA the same way the Board does — a moved
  // room reports its target CRNA (students preserved). Everything CRNA-derived below (site grouping, the card's
  // rooms, the swap targets) goes through here, so a swap made here or on the Board is reflected identically.
  const isCrnaId = (id: string): boolean => employeeById.get(id)?.employeeType === 'nurse';
  const effectiveNurseIds = (a: AllocationLike): string[] =>
    effectiveNurseIdsForRoom(a.allocatedNurseIds || [], scheduleById.get(a.operatingRoomScheduleId)?.operatingRoomId, nurseTransfers, isCrnaId);
  const nameOf = (id: string): string => { const e = employeeById.get(id); return e ? `${e.firstName} ${e.lastName}` : 'someone'; };
  const roomLabelOf = (rid?: string): string => { const r = rid ? roomById.get(rid) : undefined; return r?.name || r?.roomNumber || rid || 'a room'; };
  // The Board's hospital COLUMN order (first-seen in the operating-rooms list) — shared by the site grouping AND the
  // swap dropdown's site groups, so both match the Board.
  const boardSiteOrder = hospitalOrderFromRooms(rooms);

  const shiftByEmp = new Map<string, ShiftLike>();
  for (const s of shifts) {
    const cur = shiftByEmp.get(s.employeeId);
    if (!cur || (s.startTimeUtc || '') < (cur.startTimeUtc || '')) shiftByEmp.set(s.employeeId, s);
  }
  const workingIds = new Set(shifts.map(s => s.employeeId));
  // CRNAs = nurses (and students shown alongside) who have a shift today.
  const crnas = employees.filter(e => (e.employeeType === 'nurse' || e.employeeType.startsWith('nurse-student')) && workingIds.has(e.employeeId));

  // For each CRNA, the site(s) they're allocated to and the earliest case start at each — so a CRNA who
  // works one site then travels to another is listed under BOTH, in time order. Doctors are tracked per
  // site too (for the anesthesiologist chips on each site header).
  const hospitalByName = new Map(hospitals.map(h => [h.name, h]));
  // A colored site "coin" descriptor for the popover card (acronym on the hospital's own color).
  const coinFor = (hid: string): SiteCoinOption => { const h = hospitalById.get(hid); const name = h?.name || ''; return { hospitalId: hid, name, acronym: siteTag(name), color: colorForHospital(h) }; };
  // The CRNA's credentialed REAL sites as coins (creds that map to a current hospital name), matching the board.
  const credentialCoinsFor = (e: EmployeeLike): SiteCoinOption[] =>
    // C1 phase 2: a credential may be a hospitalId (migrated) OR a hospital name (legacy) — resolve id first, then name.
    (e.credentials || []).map(c => hospitalById.get(c) ?? hospitalByName.get(c)).filter((h): h is HospitalLike => !!h)
      .map(h => ({ hospitalId: h.hospitalId, name: h.name, acronym: siteTag(h.name), color: colorForHospital(h) }));

  const crnaSiteStart = new Map<string, Map<string, number>>(); // empId -> (hospitalId -> earliest start min)
  // Earliest CASE per (empId, site) -> drives the DERIVED report-in (30 min before, 45 if nerve block).
  const crnaSiteEarliest = new Map<string, Map<string, { startUtc: string; nerve: boolean }>>();
  // Every room the CRNA is in (with its cases) -> the popover card's "Rooms" section.
  const crnaRoomsById = new Map<string, Map<string, AssignedRoomInfo>>();
  const doctorsBySite = new Map<string, Set<string>>();
  for (const a of allocations) {
    const sched = scheduleById.get(a.operatingRoomScheduleId);
    if (!sched) continue;
    const room = roomById.get(sched.operatingRoomId);
    const hospId = room?.hospitalId;
    if (!hospId) continue;
    const startMin = localMin(sched.startTimeUtc);
    const nerve = needsNerveBlock(sched.surgeryType);
    for (const nid of effectiveNurseIds(a)) {
      if (!crnaSiteStart.has(nid)) crnaSiteStart.set(nid, new Map());
      const m = crnaSiteStart.get(nid)!;
      m.set(hospId, Math.min(m.get(hospId) ?? Infinity, startMin));
      if (!crnaSiteEarliest.has(nid)) crnaSiteEarliest.set(nid, new Map());
      const em = crnaSiteEarliest.get(nid)!;
      const cur = em.get(hospId);
      if (!cur || sched.startTimeUtc < cur.startUtc) em.set(hospId, { startUtc: sched.startTimeUtc, nerve });
      if (!crnaRoomsById.has(nid)) crnaRoomsById.set(nid, new Map());
      const rmap = crnaRoomsById.get(nid)!;
      let ri = rmap.get(sched.operatingRoomId);
      if (!ri) { ri = { operatingRoomId: sched.operatingRoomId, roomLabel: room?.name || room?.roomNumber || sched.operatingRoomId, startTimeUtc: sched.startTimeUtc, surgeryTypes: [], hasCrna: true, cases: [] }; rmap.set(sched.operatingRoomId, ri); }
      ri.cases!.push({ startTimeUtc: sched.startTimeUtc, surgeryType: sched.surgeryType || '' });
      if (sched.surgeryType) ri.surgeryTypes.push(sched.surgeryType);
      if (sched.startTimeUtc < ri.startTimeUtc) ri.startTimeUtc = sched.startTimeUtc;
    }
    for (const did of a.allocatedDoctorIds || []) {
      if (!doctorsBySite.has(hospId)) doctorsBySite.set(hospId, new Set());
      doctorsBySite.get(hospId)!.add(did);
    }
  }

  // ---- CRNA room transfer/swap (full parity with the Board card) ----
  // Targets: any CRNA on shift today who isn't marked off — placed or not (an unplaced CRNA is a valid target,
  // like the Board). Students (nurse-student*) are excluded; a swap trades one CRNA for another.
  const transferTargetCrnaIds = employees
    .filter(e => e.employeeType === 'nurse' && workingIds.has(e.employeeId) && !(anesStatus[e.employeeId] || []).includes('off'))
    .map(e => e.employeeId);
  const toCandidateList = (ids: string[], excludeId: string) => ids
    .filter(d => d !== excludeId).map(d => ({ id: d, name: nameOf(d) })).sort((a, b) => a.name.localeCompare(b.name));
  const crnaCandidatesFor = (nurseId: string) => toCandidateList(transferTargetCrnaIds, nurseId);
  // Which CRNAs are at each site (their effective rooms + hard locks) — used ONLY to GROUP the dropdown by site,
  // ordered like the Board's columns. Derived from crnaSiteStart, which is already overlay-effective.
  const crnasBySite = new Map<string, Set<string>>();
  crnaSiteStart.forEach((m, nid) => { if (!isCrnaId(nid)) return; m.forEach((_v, hid) => { if (!crnasBySite.has(hid)) crnasBySite.set(hid, new Set()); crnasBySite.get(hid)!.add(nid); }); });
  Object.entries(siteLocks).forEach(([empId, hid]) => { if (employeeById.get(empId)?.employeeType !== 'nurse') return; if (!crnasBySite.has(hid)) crnasBySite.set(hid, new Set()); crnasBySite.get(hid)!.add(empId); });
  // The effective room(s) a CRNA holds (a CRNA is single-room; a set guards against dup cases).
  const crnaRoomsForEff = (nurseId: string): string[] => {
    const startByRoom = new Map<string, string>();
    for (const a of allocations) {
      if (!effectiveNurseIds(a).includes(nurseId)) continue;
      const sched = scheduleById.get(a.operatingRoomScheduleId);
      const rid = sched?.operatingRoomId;
      if (!rid) continue;
      const st = sched?.startTimeUtc || '';
      if (!startByRoom.has(rid) || st < startByRoom.get(rid)!) startByRoom.set(rid, st);
    }
    // Ordered by first case, so "transfer all" hands over the EARLIEST room deterministically.
    return [...startByRoom.entries()].sort((x, y) => x[1].localeCompare(y[1]) || x[0].localeCompare(y[0])).map(([rid]) => rid);
  };
  // The RAW (pre-overlay) CRNA holder of each room — the other half of what the overlay rules need to keep a
  // CRNA single-room (see nurseRoomMoves.ts).
  const rawCrnaIdsByRoom = (): RawCrnaByRoom => {
    const out: RawCrnaByRoom = {};
    for (const a of allocations) {
      const rid = scheduleById.get(a.operatingRoomScheduleId)?.operatingRoomId;
      if (!rid) continue;
      out[rid] = [...new Set([...(out[rid] || []), ...(a.allocatedNurseIds || []).filter(isCrnaId)])];
    }
    return out;
  };
  const applyNurseTransfers = (next: Record<string, string>) => setNurseTransfers(saveNurseTransfers(selectedDate, next));
  // PERSIST a CRNA move/swap (rewrite allocatedNurseIds) instead of only writing the overlay — so a hand-tuned
  // in-time waterfall no longer reverts on save. Mirrors the Board card: reuse the nurseRoomMoves rules to compute
  // the end state, flush it to the allocation, clear the overlay for touched rooms, record a 'nurse' Undo entry.
  // Re-read the window, refuse on a genuine conflict, rebase onto fresh server state, then write. The overlay
  // clear, the Undo entry and the Move Log line happen ONLY after the write lands, so a refused or failed write
  // leaves the tab exactly as it was and says why. See components/allocationWriteGuard.ts.
  const persistNurseMove = async (newOverlay: Record<string, string>, undoLabel: string, log: Parameters<typeof appendMove>[1]) => {
    if (!isAdmin) return;
    const editsFor = (source: any[]) => {
      const allocs = source.map((a: any) => ({ scheduleId: a.operatingRoomScheduleId, operatingRoomId: scheduleById.get(a.operatingRoomScheduleId)?.operatingRoomId, allocatedNurseIds: a.allocatedNurseIds || [] }));
      return nurseEditsForOverlay(newOverlay, allocs, isCrnaId);
    };
    const res = buildNurseEditPayload(allocations as any, editsFor(allocations), allocationsLoaded);
    if (!res) return;
    const priorAllocations = allocations;
    setAllocations(res.nextAllocations as AllocationLike[]);
    setError('');

    const windowStart = localDateToUtcStart(selectedDate);
    const windowEnd = localDateToUtcEnd(selectedDate);
    const editedIds = editedScheduleIds(editsFor(allocations));
    const outcome = await commitAllocationEdit({
      snapshot: priorAllocations as unknown as GuardableAllocation[],
      editedIds,
      fetchFresh: async () => ((await searchAllocationsByWindow(windowStart, windowEnd)).allocations || []) as unknown as GuardableAllocation[],
      rebuild: (fresh) => buildNurseEditPayload(fresh as any, editsFor(fresh as any[]), true),
      put: (payload) => updateAllocations(payload, windowStart, windowEnd),
    });

    if (!outcome.ok) {
      setAllocations(priorAllocations);
      if (outcome.message) setError(outcome.message);
      if (outcome.reason === 'error') console.error('CRNA move save failed', (outcome as any).error);
      return;
    }

    const cleared = { ...nurseTransfers };
    for (const rid of Object.keys(newOverlay)) delete cleared[rid];
    applyNurseTransfers(cleared);
    let after: AllocationLike[] = res.nextAllocations as AllocationLike[];
    try { after = ((await searchAllocationsByWindow(windowStart, windowEnd)).allocations || []) as AllocationLike[]; } catch { /* write landed */ }
    setAllocations(after);
    setUndoStack(pushUndo(selectedDate, {
      label: undoLabel,
      kind: 'nurse',
      changes: res.undo,
      expected: (after as unknown as GuardableAllocation[]).filter(a => editedIds.includes(a.operatingRoomScheduleId)),
    }));
    appendMove(selectedDate, log);
  };
  const undoLastMove = async () => {
    if (!isAdmin) return;
    const { entry, stack } = popUndo(selectedDate);
    if (!entry) return;
    const edits = entry.kind === 'nurse'
      ? nurseEditsFromUndo(entry.changes as NurseUndoChange[])
      : editsFromUndo(entry.changes as DoctorUndoChange[]);
    const rebuild = (source: any[], loaded: boolean) => (entry.kind === 'nurse'
      ? buildNurseEditPayload(source as any, edits as any, loaded)
      : buildDoctorEditPayload(source as any, edits as any, loaded));
    const res = rebuild(allocations as any, allocationsLoaded);
    if (!res) { setUndoStack(stack); return; }
    const priorAllocations = allocations;
    setAllocations(res.nextAllocations as AllocationLike[]);
    setError('');

    const windowStart = localDateToUtcStart(selectedDate);
    const windowEnd = localDateToUtcEnd(selectedDate);
    // `changes` are ABSOLUTE prior ids; `expected` is what the move left behind. If the server no longer matches
    // it, the move was superseded (wipe, re-allocate, another runner) and replaying would overwrite, not undo.
    const snapshot = (entry.expected && entry.expected.length ? entry.expected : (allocations as unknown as GuardableAllocation[]));
    const outcome = await commitAllocationEdit({
      snapshot,
      editedIds: editedScheduleIds(edits as { scheduleId: string }[]),
      fetchFresh: async () => ((await searchAllocationsByWindow(windowStart, windowEnd)).allocations || []) as unknown as GuardableAllocation[],
      rebuild: (fresh) => rebuild(fresh as any[], true),
      put: (payload) => updateAllocations(payload, windowStart, windowEnd),
    });

    if (!outcome.ok) {
      setAllocations(priorAllocations);
      if (outcome.reason === 'conflict') setError(`That move can no longer be undone — ${entry.label} has been superseded by a newer change. Reload to see the latest board.`);
      else if (outcome.message) setError(outcome.message);
      if (outcome.reason === 'error') console.error('CRNA undo save failed', (outcome as any).error);
      return;
    }
    setUndoStack(stack);
    appendMove(selectedDate, { kind: 'transfer', detail: `Undid: ${entry.label} (CRNA tab)` });
    try { setAllocations(((await searchAllocationsByWindow(windowStart, windowEnd)).allocations || []) as AllocationLike[]); } catch { /* write landed */ }
  };
  // Move / swap write the SAME per-day nurse-transfer overlay the Board writes (so the change shows on both, live
  // via the storage listener) and journal a Move Log entry, tagged "(CRNA tab)" like the Anesthesiologists tab.
  const moveNurseRoom = (operatingRoomId: string, toNurseId: string) => {
    // The target must be RELEASED from any room they already hold — a CRNA is single-room, and writing only
    // this room's entry cloned them into two ORs (the same gap the Board's copy of this handler had).
    const overlay = moveNurseToRoom(nurseTransfers, rawCrnaIdsByRoom(), operatingRoomId, toNurseId);
    persistNurseMove(overlay, `Moved ${roomLabelOf(operatingRoomId)} CRNA to ${nameOf(toNurseId)}`,
      { kind: 'transfer', who: nameOf(toNurseId), room: roomLabelOf(operatingRoomId), detail: `Moved ${roomLabelOf(operatingRoomId)} CRNA to ${nameOf(toNurseId)} (CRNA tab)` });
  };
  const swapNurseRoom = (operatingRoomId: string, fromNurseId: string, withNurseId: string) => {
    if (fromNurseId === withNurseId) return;
    const bRoomStart = new Map<string, string>();
    for (const a of allocations) {
      if (!effectiveNurseIds(a).includes(withNurseId)) continue;
      const sched = scheduleById.get(a.operatingRoomScheduleId);
      const rid = sched?.operatingRoomId;
      if (!rid || rid === operatingRoomId) continue;   // trade B's room back regardless of site (a CRNA is single-room)
      const st = sched?.startTimeUtc || '';
      if (!bRoomStart.has(rid) || st < bRoomStart.get(rid)!) bRoomStart.set(rid, st);
    }
    const bEarliest = [...bRoomStart.entries()].sort((x, y) => x[1].localeCompare(y[1]))[0]?.[0];
    const overlay = swapNurseRooms(nurseTransfers, rawCrnaIdsByRoom(), operatingRoomId, fromNurseId, withNurseId, bEarliest);
    persistNurseMove(overlay, bEarliest ? `Swapped ${roomLabelOf(operatingRoomId)} CRNA` : `Moved ${roomLabelOf(operatingRoomId)} CRNA to ${nameOf(withNurseId)}`,
      { kind: 'transfer', who: nameOf(withNurseId), from: nameOf(fromNurseId), room: roomLabelOf(operatingRoomId), detail: bEarliest ? `Swapped ${roomLabelOf(operatingRoomId)} CRNA: ${nameOf(fromNurseId)} ⇄ ${nameOf(withNurseId)} (CRNA tab)` : `Moved ${roomLabelOf(operatingRoomId)} CRNA to ${nameOf(withNurseId)} (CRNA tab)` });
  };
  // The roomTransfer control passed to the card — identical shape to the Board's crnaRoomTransferFor.
  const crnaRoomTransferFor = (nurseId: string) => ({
    candidates: crnaCandidatesFor(nurseId),
    candidateGroups: groupTransferCandidates(
      transferTargetCrnaIds.filter(t => t !== nurseId), crnasBySite, nameOf,
      (hid: string) => hospitalNameById.get(hid) || '', boardSiteOrder,
      (hid: string) => { const cc = coinFor(hid); return { color: cc.color, acronym: cc.acronym }; },
      (cid: string) => orderOf(cid),
    ),
    getCandidateCard: (cid: string) => {
      const ce = employeeById.get(cid);
      if (!ce) return undefined;
      const cs = shiftByEmp.get(cid);
      return {
        employee: ce as unknown as Employee,
        shift: cs && cs.startTimeUtc && cs.endTimeUtc ? { startTimeUtc: cs.startTimeUtc, endTimeUtc: cs.endTimeUtc, order: orderOf(cid) ?? undefined } : undefined,
        hospitalName: ce.homeHospitalId ? hospitalNameById.get(ce.homeHospitalId) : undefined,
        assignedRooms: assignedRoomsFor(cid),
        atSites: displaySitesFor(cid).map(coinFor),
        credentialSites: credentialCoinsFor(ce),
        inTimeLabel: anesInTime[cid] ? to12h(anesInTime[cid]) : undefined,
      };
    },
    candidatesForRoom: (_rid: string) => crnaCandidatesFor(nurseId),
    onMove: (rid: string, toId: string) => moveNurseRoom(rid, toId),
    // Looping moveNurseRoom would fight itself (each call releases the target from the room the previous call
    // gave them). A CRNA is single-room, so the target takes the earliest room — see moveNurseToRooms.
    onMoveAll: (toId: string) => {
      const rids = crnaRoomsForEff(nurseId);
      if (!rids.length) return;
      const overlay = moveNurseToRooms(nurseTransfers, rawCrnaIdsByRoom(), rids, toId);
      persistNurseMove(overlay, `Moved ${roomLabelOf(rids[0])} CRNA to ${nameOf(toId)}`,
        { kind: 'transfer', who: nameOf(toId), from: nameOf(nurseId), room: roomLabelOf(rids[0]), detail: `Moved ${roomLabelOf(rids[0])} CRNA to ${nameOf(toId)} (CRNA tab)` });
    },
    onSwapAll: (otherId: string) => { const r = crnaRoomsForEff(nurseId)[0]; if (r) swapNurseRoom(r, nurseId, otherId); },
    onSwapRoom: (rid: string, otherId: string) => swapNurseRoom(rid, nurseId, otherId),
  });

  // DERIVED report-in ("In") for a CRNA at a site — 30 min before that site's earliest case (45 if nerve block).
  const reportInHMForSite = (id: string, site: string): string => {
    const e = crnaSiteEarliest.get(id)?.get(site);
    if (!e) return '';
    const lead = e.nerve ? NERVE_BLOCK_LEAD_MIN : REPORT_IN_LEAD_MIN;
    return utcToLocalTime(new Date(new Date(e.startUtc).getTime() - lead * 60000).toISOString());
  };
  // The CRNA's rooms (with cases) for the popover card, earliest first.
  const assignedRoomsFor = (id: string): AssignedRoomInfo[] => {
    const rmap = crnaRoomsById.get(id);
    if (!rmap) return [];
    return [...rmap.values()]
      .map(r => ({ ...r, cases: (r.cases || []).slice().sort((a, b) => a.startTimeUtc.localeCompare(b.startTimeUtc)) }))
      .sort((a, b) => a.startTimeUtc.localeCompare(b.startTimeUtc));
  };
  const orderOf = (id: string): number | null => { const o = shiftByEmp.get(id)?.order; return o === undefined || o === null ? null : o; };
  // A CRNA's sites in the order they work them (by earliest case start at each site).
  const sitesOrderedFor = (id: string): string[] => {
    const m = crnaSiteStart.get(id);
    if (!m) return [];
    return [...m.entries()].sort((a, b) => a[1] - b[1]).map(e => e[0]);
  };
  // Sites to LIST a CRNA under (grouping + coins): their allocated sites in work order, then any FLOAT site they
  // hold that they aren't otherwise working. A float has no case start, so it sorts after real work.
  const displaySitesFor = (id: string): string[] => {
    const alloc = sitesOrderedFor(id);
    const floats = [...(floatSitesByEmp.get(id) || [])].filter(h => !alloc.includes(h));
    return [...alloc, ...floats];
  };
  // The CRNA's single report-in for the whole day (used by the shift-vs-pecking "group" view, which isn't
  // site-scoped): floats always report at FLOAT_IN_HM; otherwise 30 min before their earliest case anywhere
  // (45 if it needs a nerve block); an unallocated CRNA falls back to their shift start.
  const dayReportInHM = (id: string): string => {
    if (isFloatAnywhere(id)) return FLOAT_IN_HM;
    let earliest: { startUtc: string; nerve: boolean } | undefined;
    crnaSiteEarliest.get(id)?.forEach(v => { if (!earliest || v.startUtc < earliest.startUtc) earliest = v; });
    if (earliest) {
      const lead = earliest.nerve ? NERVE_BLOCK_LEAD_MIN : REPORT_IN_LEAD_MIN;
      return utcToLocalTime(new Date(new Date(earliest.startUtc).getTime() - lead * 60000).toISOString());
    }
    const s = shiftByEmp.get(id);
    const hasReal = !!(s && s.startTimeUtc && s.endTimeUtc && localMin(s.startTimeUtc) !== localMin(s.endTimeUtc));
    if (hasReal) return localHM(s!.startTimeUtc);        // shift-worker: their availability start
    if (orderOf(id) !== null) return BENCH_IN_HM;        // benched PECKING CRNA: the 11:30 bench
    return '';
  };
  // Pre-programmed travel note: if this isn't the CRNA's last site, they travel to the next one.
  const travelNoteFor = (id: string, site: string): string | null => {
    const sites = sitesOrderedFor(id);
    const idx = sites.indexOf(site);
    if (idx < 0 || idx === sites.length - 1) return null;
    const next = sites[idx + 1];
    const nextName = hospitalNameById.get(next) || next;
    const t = crnaSiteStart.get(id)?.get(next);
    const hhmm = (t === undefined || t === Infinity) ? '' : `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
    return hhmm ? `Travel to ${nextName} by ${to12h(hhmm)}` : `Travel to ${nextName}`;
  };

  // ---- Group CRNAs by site (listed under EACH site they work) ----
  const bySite = new Map<string, EmployeeLike[]>();
  for (const c of crnas) {
    const sites = displaySitesFor(c.employeeId);
    if (sites.length === 0) {
      if (!bySite.has(UNASSIGNED)) bySite.set(UNASSIGNED, []);
      bySite.get(UNASSIGNED)!.push(c);
    } else {
      for (const site of sites) {
        if (!bySite.has(site)) bySite.set(site, []);
        bySite.get(site)!.push(c);
      }
    }
  }
  const outMinOf = (id: string) => localMin(shiftByEmp.get(id)?.endTimeUtc);
  // Order within a site: pecking-order CRNAs by pecking # (so their derived in-times read #1 earliest → last
  // latest, mirroring the Anesthesiologists tab); CRNAs with no pecking order fall back to time OFF, then name.
  const byPeckingThenOut = (a: EmployeeLike, b: EmployeeLike) => {
    const oa = orderOf(a.employeeId), ob = orderOf(b.employeeId);
    if (oa !== null || ob !== null) {
      if (oa === null) return 1;
      if (ob === null) return -1;
      return (oa - ob) || a.lastName.localeCompare(b.lastName);
    }
    return outMinOf(a.employeeId) - outMinOf(b.employeeId) || a.lastName.localeCompare(b.lastName);
  };
  // Assigned sites first in the BOARD's column order (first-seen hospitalId in the operating-rooms list, via
  // hospitalOrderFromRooms), unassigned last. This keeps the CRNA tab from leading with a different hospital
  // than the board (e.g. Downtown-first here vs Uptown-first on the board); a site the board doesn't know falls
  // back to name order.
  const boardSiteCompare = compareByHospitalOrder(
    boardSiteOrder,
    (a, b) => (hospitalNameById.get(a) || a).localeCompare(hospitalNameById.get(b) || b),
  );
  const siteGroups = [...bySite.keys()]
    .sort((a, b) => {
      if (a === UNASSIGNED) return 1; if (b === UNASSIGNED) return -1;
      return boardSiteCompare(a, b);
    })
    .map(site => ({ site, name: site === UNASSIGNED ? 'Unassigned' : (hospitalNameById.get(site) || site), items: bySite.get(site)!.slice().sort(byPeckingThenOut) }));

  const crnaTypeLabel = (t: string) => t === 'nurse' ? 'CRNA' : 'SRNA';

  // Plain render FUNCTIONS, invoked as `renderNote(...)` — NOT components rendered as `<Note/>`. A component
  // declared in this body gets a fresh identity each render, so React remounts it and the note <input> would
  // lose focus after every keystroke. Inlining the JSX keeps element identity stable. `renderNote` closes over
  // `locked`, which is why it stays here rather than moving to module scope.
  const renderTh = (children: React.ReactNode, w?: string): JSX.Element => (
    <th style={{ textAlign: 'left', fontSize: '0.68em', textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--text-faint)', fontWeight: 700, padding: '4px 8px', width: w }}>{children}</th>
  );

  const renderNote = (value: string, onChange: (t: string) => void): JSX.Element => (
    locked
      ? <span style={{ fontSize: '0.78em', color: 'var(--text-secondary)', fontStyle: value ? 'italic' : 'normal' }}>{value || <span style={{ color: 'var(--text-faint)' }}>—</span>}</span>
      : <input value={value} onChange={e => onChange(e.target.value)} placeholder="Add a note…" className="form-control" style={{ fontSize: '0.76em', padding: '2px 6px', width: '100%' }} />
  );

  // One CRNA row (columns: Full Name · In · Out · Shift · Location · Notes). Shared by BOTH views so a swap made
  // on either shows on both and the columns line up. `rin` is the report-in to show (site-derived, day-derived,
  // or FLOAT_IN_HM); `sites` drives the coins/card; `float` shows the Float chip; `badge` is the pecking number.
  const renderCrnaRow = (
    c: EmployeeLike,
    opts: { rin: string; sites: string[]; travel: string | null; float: boolean; keyPrefix: string; badge?: string; editable?: boolean },
  ): JSX.Element => {
    const { rin, sites, travel, float, keyPrefix, badge, editable } = opts;
    const s = shiftByEmp.get(c.employeeId);
    const inHM = localHM(s?.startTimeUtc), outHM = localHM(s?.endTimeUtc);
    const hasReal = !!(inHM && outHM && localMin(s?.startTimeUtc) !== localMin(s?.endTimeUtc));
    // A manual report-in override (this tab, the doctor tab, or the board) wins over the derived time.
    const isInOverride = !!anesInTime[c.employeeId];
    const effRin = anesInTime[c.employeeId] || rin;
    const inDisplay = effRin ? to12h(effRin) : (hasReal ? to12h(inHM) : '—');
    // Marked "Off" (sent home) on the card grays this row — the name stays on the list, greyed out (mirrors the
    // Anesthesiologists tab). The disposition lives in the shared oc-anes store, so an Off set on the Board dims here too.
    const sentHome = (anesStatus[c.employeeId] || []).includes('off');
    return (
      <tr key={`${keyPrefix}-${c.employeeId}`} style={{ borderBottom: '1px solid var(--border-subtle)', opacity: sentHome ? 0.45 : 1, filter: sentHome ? 'grayscale(0.6)' : undefined }}>
        <td style={{ padding: '5px 8px', fontSize: '0.85em', fontWeight: 600 }}>
          {badge && <span style={{ marginRight: '7px', fontSize: '0.85em', fontWeight: 800, color: 'var(--amber)' }} title="Pecking order #">{badge}</span>}
          {/* Click the name for the SAME provider card the Board & Anesthesiologists tab show. */}
          <EmployeeDetailsPopover
            employee={c as unknown as Employee}
            shift={s && s.startTimeUtc && s.endTimeUtc ? { startTimeUtc: s.startTimeUtc, endTimeUtc: s.endTimeUtc, order: orderOf(c.employeeId) ?? undefined } : undefined}
            hospitalName={c.homeHospitalId ? hospitalNameById.get(c.homeHospitalId) : undefined}
            assignedRooms={assignedRoomsFor(c.employeeId)}
            atSites={sites.map(coinFor)}
            credentialSites={credentialCoinsFor(c)}
            inTimeLabel={effRin ? to12h(effRin) : undefined}
            boardActivityCoin
            {...(c.employeeType === 'nurse' ? {
              statusControl: { statuses: anesStatus[c.employeeId] || [], note: anesNotes[c.employeeId] || '', onToggleStatus: (st: DoctorDayStatus) => toggleAnesStatus(c.employeeId, st), onSetNote: (t: string) => setAnesNote(c.employeeId, t) },
              roomTransfer: crnaRoomTransferFor(c.employeeId),
            } : {})}
            triggerStyle={{ display: 'inline-block', maxWidth: '100%', textDecoration: 'none' }}
          >
            <span style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {c.firstName} {c.lastName}
              {c.employeeType !== 'nurse' && <span style={{ marginLeft: '6px', fontSize: '0.7em', color: 'var(--text-muted)', fontWeight: 700 }}>{crnaTypeLabel(c.employeeType)}</span>}
            </span>
          </EmployeeDetailsPopover>
        </td>
        <td style={{ padding: '5px 8px', fontSize: '0.8em' }}>
          {/* The override is editable wherever it is SHOWN, never only where it was set. Scoping the input to
              the Unassigned group made it a one-way door: set 11:30 on an unassigned CRNA, allocate, and she
              moves under her site — where the row still publishes 11:30 over the site-derived report-in with
              no control anywhere that can clear it. Gated on `locked` like every other mutating control on
              this tab (a read-only account can never unlock). An override is marked, and clearing the field
              falls back to the derived time — same contract as the Anesthesiologists tab's "⟳ Auto". */}
          {editable && !locked ? (
            <input
              type="time"
              value={effRin || (hasReal ? inHM : '')}
              onChange={e => setCrnaInTime(c.employeeId, e.target.value)}
              title="Set this CRNA's report-in time (clear it to go back to the derived time)"
              style={{ fontSize: '0.95em', padding: '1px 4px', border: `1px solid ${isInOverride ? 'var(--or-blue-mid)' : 'var(--border-subtle)'}`, borderRadius: '4px', background: 'var(--bg-input)', color: isInOverride ? 'var(--or-blue-mid)' : 'var(--text-primary)', width: '96px', cursor: 'pointer' }}
            />
          ) : (
            <span style={isInOverride ? { color: 'var(--or-blue-mid)', fontWeight: 600 } : undefined} title={isInOverride ? 'Manually set report-in time' : undefined}>{inDisplay}</span>
          )}
        </td>
        <td style={{ padding: '5px 8px', fontSize: '0.8em', color: 'var(--text-muted)' }}>{hasReal ? to12h(outHM) : '—'}</td>
        <td style={{ padding: '5px 8px', fontSize: '0.78em', color: 'var(--text-secondary)' }}>{shiftLabel(s) || '—'}</td>
        <td style={{ padding: '5px 8px', fontSize: '0.78em' }}>
          {float && (
            <div title="Assigned to float this day — the whole-site relief runner; reports at 6:00a" style={{ marginBottom: '3px', display: 'inline-block', fontSize: '0.82em', fontWeight: 700, color: '#fff', background: NAMEPLATE_COLORS.crnaOut5pm, borderRadius: '9px', padding: '1px 8px', whiteSpace: 'nowrap' }}>
              Float
            </div>
          )}
          {travel && (
            <div title="Pre-programmed travel instruction" style={{ marginTop: '3px', display: 'inline-block', fontSize: '0.82em', fontWeight: 700, color: '#fff', background: 'var(--amber)', borderRadius: '9px', padding: '1px 7px', whiteSpace: 'nowrap' }}>
              → {travel}
            </div>
          )}
          {/* Click the coin to lock this CRNA to any site for the day (no unlock needed). */}
          <div style={{ marginTop: '4px' }}>
            {renderSiteCoin(c.employeeId, sites)}
          </div>
        </td>
        <td style={{ padding: '5px 8px' }}>{renderNote(anno.notes[c.employeeId] || '', t => setNote(c.employeeId, t))}</td>
      </tr>
    );
  };

  // ---- "Group" view data (shift work vs pecking order) — mirrors the Anesthesiologists tab so report-in times
  // can be compared straight down an ordered list, independent of which sites they're at. ----
  const crnasNoOrder = crnas.filter(c => orderOf(c.employeeId) === null);
  const peckingCrnas = crnas.filter(c => orderOf(c.employeeId) !== null)
    .slice().sort((a, b) => (orderOf(a.employeeId)! - orderOf(b.employeeId)!) || a.lastName.localeCompare(b.lastName));
  // Shift-work CRNAs grouped by their shift WINDOW (earliest-out first), exactly like the doctor tab's fixed groups.
  const crnaWindowGroups = (() => {
    const groups = new Map<string, { label: string; startMin: number; endMin: number; items: EmployeeLike[] }>();
    for (const c of crnasNoOrder) {
      const s = shiftByEmp.get(c.employeeId);
      const lbl = shiftLabel(s) || 'No shift';
      if (!groups.has(lbl)) groups.set(lbl, { label: lbl, startMin: localMin(s?.startTimeUtc), endMin: localMin(s?.endTimeUtc), items: [] });
      groups.get(lbl)!.items.push(c);
    }
    return [...groups.values()].sort((a, b) => a.endMin - b.endMin || a.startMin - b.startMin);
  })();
  const renderGroupHeader = (title: string, count: number, headColor: string): JSX.Element => (
    <div style={{ background: headColor, color: '#fff', fontWeight: 700, fontSize: '0.78em', letterSpacing: '0.4px', textTransform: 'uppercase', padding: '6px 10px', display: 'flex', justifyContent: 'space-between' }}>
      <span>{title}</span><span style={{ opacity: 0.9 }}>{count}</span>
    </div>
  );
  const groupThead = (
    <thead>
      <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
        {renderTh('Full Name', '22%')}{renderTh('In', '8%')}{renderTh('Out', '8%')}{renderTh('Shift', '16%')}{renderTh('Location', '14%')}{renderTh('Notes', '32%')}
      </tr>
    </thead>
  );
  const groupRowFor = (c: EmployeeLike, keyPrefix: string, badge?: string): JSX.Element =>
    renderCrnaRow(c, { rin: dayReportInHM(c.employeeId), sites: displaySitesFor(c.employeeId), travel: null, float: isFloatAnywhere(c.employeeId), keyPrefix, badge, editable: true });

  const setView = (m: 'site' | 'group') => { setViewMode(m); bumpAutoLock(); };

  return (
    <div style={{ padding: '8px 4px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '26px', fontFamily: 'var(--font-display)', fontWeight: 700 }}>CRNAs</h1>
        <input type="date" className="form-control" style={{ maxWidth: '180px' }} value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
        <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>{crnas.length} working</span>
        {/* Undo the last persisted move/swap (CRNA moves now rewrite the allocation; this reverses them). */}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={undoStack.length === 0}
          onClick={undoLastMove}
          title={undoStack.length ? `Undo: ${undoStack[undoStack.length - 1].label}` : 'Nothing to undo'}
        >↩ Undo{undoStack.length > 1 ? ` (${undoStack.length})` : ''}</button>
        {/* View switch: per-site (default) vs a shift-work-and-pecking-order layout for comparing report-in times. */}
        <div style={{ display: 'inline-flex', border: '1px solid var(--border-subtle)', borderRadius: '8px', overflow: 'hidden' }}>
          {([['site', 'By site'], ['group', 'By group']] as const).map(([m, label]) => (
            <button key={m} type="button" onClick={() => setView(m)}
              title={m === 'group' ? 'Group by shift work and pecking order to compare report-in times' : 'Group by hospital site'}
              style={{ border: 'none', padding: '4px 11px', fontSize: '0.78em', fontWeight: 700, cursor: 'pointer',
                background: viewMode === m ? 'var(--or-blue-mid)' : 'transparent', color: viewMode === m ? '#fff' : 'var(--text-secondary)' }}>
              {label}
            </button>
          ))}
        </div>
        {isAdmin && <LockToggle locked={locked} onChange={(nextLocked) => (nextLocked ? lockNow() : unlock())} />}
      </div>

      {!isAdmin && <ReadOnlyBanner />}
      {error && <div style={{ color: 'var(--danger-text, #dc2626)', marginBottom: '10px' }}>{error}</div>}
      {loading ? (
        <div style={{ color: 'var(--text-muted)' }}>Loading…</div>
      ) : crnas.length === 0 ? (
        <div style={{ color: 'var(--text-muted)' }}>No CRNAs have shifts on this day.</div>
      ) : viewMode === 'group' ? (
        <div style={{ overflowX: 'auto' }}>
          {/* Shift-work section — grouped by shift window (earliest-out first), mirroring the Anesthesiologists tab. */}
          <div style={{ marginBottom: '18px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${NAMEPLATE_COLORS.crnaOut5pm}` }}>
            {renderGroupHeader('Shift work', crnasNoOrder.length, NAMEPLATE_COLORS.crnaOut5pm)}
            <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse', background: 'var(--bg-surface)' }}>
              {groupThead}
              <tbody>
                {crnasNoOrder.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: '8px 10px', fontSize: '0.8em', color: 'var(--text-faint)' }}>No shift-work CRNAs today.</td></tr>
                ) : crnaWindowGroups.map(wg => (
                  <React.Fragment key={wg.label}>
                    <tr><td colSpan={6} style={{ fontSize: '0.66em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', color: 'var(--text-faint)', padding: '6px 10px 2px', background: 'var(--bg-surface-raised)' }}>{wg.label}</td></tr>
                    {wg.items.slice().sort((a, b) => a.lastName.localeCompare(b.lastName)).map(c => groupRowFor(c, `shift-${wg.label}`))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pecking-order section — numbered 1..N by shift order; report-in ramps down the list. */}
          <div style={{ marginBottom: '18px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${NAMEPLATE_COLORS.docSupervise}` }}>
            {renderGroupHeader('Pecking order', peckingCrnas.length, NAMEPLATE_COLORS.docSupervise)}
            <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse', background: 'var(--bg-surface)' }}>
              {groupThead}
              <tbody>
                {peckingCrnas.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: '8px 10px', fontSize: '0.8em', color: 'var(--text-faint)' }}>No pecking-order CRNAs today.</td></tr>
                ) : peckingCrnas.map((c, i) => groupRowFor(c, 'peck', `${i + 1}`))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: '0.72em', color: 'var(--text-faint)', marginTop: '4px' }}>
            Shift-work CRNAs (by shift window) then pecking order (by #), so you can compare report-in times straight down each ordered list. Floats report at 6:00a. Switch to “By site” to see who’s at each hospital.
          </div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {siteGroups.map(g => {
            const color = g.site === UNASSIGNED ? 'var(--text-muted)' : colorForHospital(hospitalById.get(g.site));
            const docChips = [...(doctorsBySite.get(g.site) || [])]
              .map(id => employeeById.get(id))
              .filter(Boolean)
              .sort((a, b) => a!.lastName.localeCompare(b!.lastName));
            return (
              <div key={g.site} style={{ marginBottom: '18px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${color}` }}>
                {/* Site header: name + count + anesthesiologist chips at this site. */}
                <div style={{ background: color, color: '#fff', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: '0.85em', letterSpacing: '0.3px' }}>{g.name}</span>
                  <span style={{ fontSize: '0.72em', opacity: 0.9 }}>{g.items.length} CRNA{g.items.length === 1 ? '' : 's'}</span>
                  {docChips.length > 0 && (
                    <span style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.66em', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Anesthesiologists:</span>
                      {docChips.map(d => (
                        <span key={d!.employeeId} style={{ fontSize: '0.7em', fontWeight: 600, background: 'rgba(255,255,255,0.25)', borderRadius: '9px', padding: '1px 7px' }}>{d!.lastName}</span>
                      ))}
                    </span>
                  )}
                </div>
                <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse', background: 'var(--bg-surface)' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      {renderTh('Full Name', '22%')}{renderTh('In', '8%')}{renderTh('Out', '8%')}{renderTh('Shift', '16%')}{renderTh('Location', '14%')}{renderTh('Notes', '32%')}
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map(c => {
                      const floatHere = isFloatAtSite(c.employeeId, g.site);
                      // DERIVED report-in for THIS site (30 min before its earliest case), or the fixed 06:00 for a
                      // float slot; the shared renderer falls back to the raw shift start when neither applies.
                      const isUnassigned = g.site === UNASSIGNED;
                      // Assigned rows: report-in derived at THIS site. Unassigned rows: the DAY-level derived time
                      // (shift start, or the 11:30 bench for an unused pecking CRNA). Every row's In cell is
                      // editable so an override can always be cleared from wherever it is displayed — a CRNA
                      // moves out of Unassigned the moment she is allocated, and an override set there would
                      // otherwise be stranded on top of her site-derived time.
                      const rin = floatHere ? FLOAT_IN_HM : (isUnassigned ? dayReportInHM(c.employeeId) : reportInHMForSite(c.employeeId, g.site));
                      return renderCrnaRow(c, {
                        rin,
                        sites: displaySitesFor(c.employeeId),
                        travel: travelNoteFor(c.employeeId, g.site),
                        float: floatHere,
                        keyPrefix: g.site,
                        editable: true,
                      });
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
          <div style={{ fontSize: '0.72em', color: 'var(--text-faint)', marginTop: '4px' }}>
            Grouped by assigned site (color-coded). Unlock to edit notes — changes save locally and auto-lock after 30 seconds. Sites and anesthesiologist chips fill in after you run Allocate staff on the Board.
          </div>
        </div>
      )}
    </div>
  );
};

export default CrnaView;
